import { useCallback } from 'react';

import { TaxonomyType } from '@revelio/data-access';

import { showAlertDialog } from '../../shared/components/alert-dialog';
import { kibanaLogger } from '../../shared/logging/kibana-logger';
import { useTaxonomyLatestVersion } from '../../shared/selection-lists/hooks/useTaxonomyVersions';
import type { DataBuilderModel } from './api/data-builder-api.types';
import { useDataBuilderQuery } from './api/useDataBuilderQuery';
import {
  type DataBuilderStore,
  dataBuilderStore,
  type ModelVersionSelections,
} from './data-builder.store';
import { getVisibleVersionOptions } from './steps/step2-column/model-utils';
import { isV3Taxonomy } from './utils/job-taxonomy-version-migration';

export type EditWarningTarget =
  | { kind: 'company'; instanceId: string }
  | { kind: 'role' }
  | { kind: 'skill' };

type RoleOrSkillKind = 'role' | 'skill';

type TaxonomyFieldConfig = {
  majorKey: keyof ModelVersionSelections;
  patchKey: keyof ModelVersionSelections;
  noun: string;
};

const TAXONOMY_FIELDS: Record<RoleOrSkillKind, TaxonomyFieldConfig> = {
  role: {
    majorKey: 'job_taxonomy_version',
    patchKey: 'job_taxonomy_patch_version',
    noun: 'role',
  },
  skill: {
    majorKey: 'skill_taxonomy_version',
    patchKey: 'skill_taxonomy_patch_version',
    noun: 'skill',
  },
};

const COMPANY_WARNING = {
  header: 'Editing Replaces All Selected Companies',
  body: "These companies were added with Map Companies, so they can't be edited individually. Saving changes here replaces the entire company selection.",
};

const cap = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const warningKey = (target: EditWarningTarget): string =>
  target.kind === 'company' ? `company:${target.instanceId}` : target.kind;

// Company warns while the population is an opaque loaded mapping — a mapper with
// no visible selections, so editing replaces everything we can't show. Fresh
// "select companies" populate companySelections and common sets use companySets
// (mapper stays null), so both fall through without a warning.
const companyEditNeedsWarning = (
  instanceId: string,
  state: DataBuilderStore
): boolean => {
  const config = state.configByInstance[instanceId];
  if (config === undefined) {
    return false;
  }
  return (
    config.companyMapper !== null &&
    Object.keys(config.companySelections).length === 0
  );
};

type TaxonomyEditOutcome =
  // Safe to edit as-is (v2, or already on the latest version).
  | { type: 'ok' }
  // v3 and the saved patch version is behind the latest, but the latest is
  // selectable here — bump to it (which prunes the stale selections) on confirm.
  | { type: 'update'; patchKey: keyof ModelVersionSelections; latest: string }
  // v3 and behind the latest, but the latest isn't even offered here — the
  // DataBuilder schema is itself out of date, so the user can't fix it. Block.
  | { type: 'block'; noun: string; saved: string; latest: string };

const resolveTaxonomyEdit = (
  kind: RoleOrSkillKind,
  state: DataBuilderStore,
  latestVersion: string | undefined,
  models: DataBuilderModel[] | undefined
): TaxonomyEditOutcome => {
  const { majorKey, patchKey, noun } = TAXONOMY_FIELDS[kind];

  // v2 taxonomies are not patch-gated — edit freely.
  const major = state.modelVersions[majorKey]?.toString();
  if (!isV3Taxonomy(major)) {
    return { type: 'ok' };
  }

  // Without both versions we can't establish a mismatch — allow the edit.
  const savedPatch = state.modelVersions[patchKey];
  if (savedPatch == null || latestVersion == null) {
    return { type: 'ok' };
  }

  const saved = String(savedPatch);
  if (saved === latestVersion) {
    return { type: 'ok' };
  }

  // Stale. Reachable when the latest version is offered as an option (the schema
  // is current); block only on positive evidence that it isn't — a missing patch
  // model means we can't prove the schema is behind, so don't hard-stop the user.
  const patchModel = models?.find((model) => model.key === patchKey);
  const reachable =
    patchModel === undefined ||
    getVisibleVersionOptions(patchModel, state.modelVersions).some(
      (option) => String(option.value) === latestVersion
    );

  return reachable
    ? { type: 'update', patchKey, latest: latestVersion }
    : { type: 'block', noun, saved, latest: latestVersion };
};

/**
 * Guards edits to Company / Role / Skill selections.
 *
 * - Company: an opaque loaded mapping warns that editing replaces the whole
 *   selection; confirm to proceed (acknowledged so it doesn't nag again).
 * - Role / Skill: if the dataset is on an older v3 taxonomy patch than the
 *   latest, editing isn't done in place. When the latest is selectable we offer
 *   to update to it (which clears the stale selections to re-pick); when the
 *   schema itself is behind the latest we hard-block and log it, since there's
 *   nothing the user can do. v2 and up-to-date selections edit normally.
 *
 * `proceed` (open the editor) only runs when editing is safe — the update and
 * block paths intentionally don't reopen it.
 */
export const useLoadedEditWarning = () => {
  const { data } = useDataBuilderQuery();
  const models = data?.models;
  const { version: latestRoleVersion } = useTaxonomyLatestVersion({
    taxonomyType: TaxonomyType.Role,
  });
  const { version: latestSkillVersion } = useTaxonomyLatestVersion({
    taxonomyType: TaxonomyType.Skill,
  });

  const guardEdit = useCallback(
    (target: EditWarningTarget, proceed: () => void) => {
      const state = dataBuilderStore.getState();

      if (target.kind === 'company') {
        const key = warningKey(target);
        const shouldWarn =
          !state.acknowledgedEditWarnings.includes(key) &&
          companyEditNeedsWarning(target.instanceId, state);
        if (!shouldWarn) {
          proceed();
          return;
        }
        showAlertDialog({
          alertType: 'confirm',
          isOpen: true,
          status: 'warning',
          headerText: COMPANY_WARNING.header,
          bodyContent: <span>{COMPANY_WARNING.body}</span>,
          footerAcceptText: 'Continue Editing',
          footerAcceptColorScheme: 'orange',
          onAccept: () => {
            dataBuilderStore.actions.acknowledgeEditWarning(key);
            proceed();
          },
        });
        return;
      }

      const latestVersion =
        target.kind === 'role' ? latestRoleVersion : latestSkillVersion;
      const outcome = resolveTaxonomyEdit(
        target.kind,
        state,
        latestVersion,
        models
      );

      if (outcome.type === 'ok') {
        proceed();
        return;
      }

      if (outcome.type === 'update') {
        const { noun } = TAXONOMY_FIELDS[target.kind];
        showAlertDialog({
          alertType: 'confirm',
          isOpen: true,
          status: 'warning',
          headerText: `Update ${cap(noun)} Taxonomy to Continue`,
          bodyContent: (
            <span>
              These {noun}s use an older taxonomy version. Editing updates the
              dataset to the latest version, which clears the current {noun}{' '}
              selection so you can re-pick it on the latest taxonomy.
            </span>
          ),
          footerAcceptText: 'Update to Latest',
          footerAcceptColorScheme: 'orange',
          onAccept: () => {
            dataBuilderStore.actions.setModelVersion(
              outcome.patchKey,
              outcome.latest
            );
          },
        });
        return;
      }

      kibanaLogger.error(
        `data-builder ${outcome.noun} taxonomy schema is behind the latest published version (schema max < latest): saved=${outcome.saved} latest=${outcome.latest}`,
        { context: 'data-builder-taxonomy-version' }
      );
      showAlertDialog({
        alertType: 'confirm',
        isOpen: true,
        status: 'error',
        headerText: `${cap(outcome.noun)} Taxonomy Out of Date`,
        bodyContent: (
          <span>
            Our team is aware and is working on updating the {outcome.noun}{' '}
            taxonomy. Please try again later.
          </span>
        ),
        footerAcceptText: 'OK',
        hideCancel: true,
      });
    },
    [latestRoleVersion, latestSkillVersion, models]
  );

  // Marks a field acknowledged so its warning doesn't nag again this session.
  // Used by the company apply paths (role/skill resolve via the version update).
  const acknowledgeEdit = useCallback((target: EditWarningTarget) => {
    dataBuilderStore.actions.acknowledgeEditWarning(warningKey(target));
  }, []);

  return { guardEdit, acknowledgeEdit };
};
